import { useState, useRef, type ChangeEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import CreateUser from "../../components/Users/CreateUser";
import UserProfileCard from "../../components/UserProfileCard";
import defaultProfileImage from "../../assets/profile-img.svg";
import { ADD_DESCRIPTION, getProfileImageExtensionError } from "../../utils/util";

const AddUser = () => {
  const navigate = useNavigate();
  const [profileImageName, setProfileImageName] = useState("");
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(
    null,
  );
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [profileImageError, setProfileImageError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleClose = () => navigate("/user-management");
  const refreshUsers = () => { };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const input = event.target;

    if (!file) {
      setProfileImageName("");
      setProfileImagePreview(null);
      setProfileImageFile(null);
      setProfileImageError(null);
      return;
    }

    const extensionError = getProfileImageExtensionError(file);
    if (extensionError) {
      setProfileImageError(extensionError);
      setProfileImageName("");
      setProfileImagePreview(null);
      setProfileImageFile(null);
      if (input) input.value = "";
      return;
    }

    setProfileImageError(null);
    setProfileImageName(file.name);
    setProfileImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="">
      <UserProfileCard />
      <Link
        to="/user-management"
        className="inline-flex items-center gap-2 text-[#E761A0] hover:underline font-semibold mb-3 text-lg"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Users
      </Link>

      <div>
        <h1 className="text-[34px] lg:text-[38px] xl:text-[56px font-display font-light text-white mb-[15px] leading-[76px]">
          Add User
        </h1>
        <p className="text-white max-w-[863px] text-lg font-light">
          {ADD_DESCRIPTION}
        </p>
      </div>

      <div className="flex xl:grid flex-col-reverse grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mt-[50px]">
        <div className="xl:max-w-[548px]">
          <CreateUser
            isEditMode={false}
            refershUsers={refreshUsers}
            profileImage={profileImagePreview}
            profileImageFile={profileImageFile}
            setIsModalOpen={(open) => {
              if (!open) handleClose();
            }}
            submitButtonText="SUBMIT"
          />
        </div>

        <div className="flex flex-col">
          <label className="block text-base text-white  font-light mb-6">
            Profile image
          </label>
          <div className="flex flex-col items-start gap-[30px]">
            <div className="w-[218px] h-[218px]  bg-transparent rounded-full border border-[#E761A0] flex items-center justify-center overflow-hidden shrink-0 p-2">
              <img
                src={(profileImagePreview ?? defaultProfileImage) || undefined}
                alt="Profile"
                className={`${profileImagePreview ? "w-full h-full" : "w-[101px] h-[102px]"} bg-black object-cover rounded-full`}
              />
            </div>
            <div className="flex w-full max-w-[549px] h-[53px] rounded-full border border-[#868686] overflow-hidden  ">
              <button
                type="button"
                onClick={handleFileButtonClick}
                className="browse-file-btn shrink-0 px-9 h-full bg-white text-gray-800 text-base rounded-full font-semibold uppercase tracking-wide hover:bg-gray-100 transition-colors"
              >
                BROWSE FILE
              </button>
              <input
                type="text"
                readOnly
                placeholder="No file chosen"
                value={profileImageName}
                // onChange={handleFileChange}
                className="flex-1 min-w-0 h-full px-4 bg-transparent text-gray-400 text-sm placeholder-gray-500 focus:outline-none border-0"
              />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={handleFileChange}
            />
            {profileImageError && (
              <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                {profileImageError}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
